/**
 * SkillRingsCard.jsx — Feb 09 2026, Lane 2 (Subscriber Home restructure).
 *
 * Coverage snapshot at a glance — the seven Ontario transferable skills
 * rendered as progress rings, always all seven, always in the canonical
 * order. Zero-coverage skills render with an empty track (not hidden)
 * so the spine stays visible.
 *
 * Data source: `coverage.by_transferable_skill` (map skill-name → count)
 * and `coverage.total_activities` (single canonical denominator, same
 * Phase-2 discipline used by CoverageBars). No new backend.
 *
 * Layout at 390px: 3 rings per row via `grid-cols-3`, upgrading to 4
 * per row at `sm:` (≥640px). The seventh ring is centred inside a
 * `flex justify-center` wrap so it never hangs on the left.
 */
import { Link } from "react-router-dom";
import { toShortSkillName } from "../../lib/skillNames";

// Canonical library order — mirrors backend
// `skill_in_the_wild.LIBRARY_NAMES`. Used to guarantee stable ring
// positions even when the coverage response omits a zero-count skill.
export const CANONICAL_SKILLS = [
  "Critical Thinking & Problem Solving",
  "Innovation, Creativity & Entrepreneurship",
  "Self-Directed Learning",
  "Collaboration",
  "Communication",
  "Global Citizenship & Sustainability",
  "Digital Literacy",
];

// Coverage code sometimes emits the "and" canonical form; map both
// spellings to the same short label.
const _ALIAS = {
  "Critical Thinking and Problem Solving": "Critical Thinking & Problem Solving",
  "Innovation, Creativity, and Entrepreneurship": "Innovation, Creativity & Entrepreneurship",
  "Global Citizenship and Sustainability": "Global Citizenship & Sustainability",
};

const _resolveCount = (byName, canonical) => {
  if (!byName) return 0;
  if (byName[canonical] != null) return Number(byName[canonical]) || 0;
  for (const [aliasFull, canon] of Object.entries(_ALIAS)) {
    if (canon === canonical && byName[aliasFull] != null) {
      return Number(byName[aliasFull]) || 0;
    }
  }
  return 0;
};

/** Single ring — SVG circle with mint fill on a light track. */
const Ring = ({ skill, count, total }) => {
  const size = 72;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const denom = Math.max(1, Number(total) || 0);
  const proportion = Math.max(0, Math.min(1, count / denom));
  const dash = circumference * proportion;
  const short = toShortSkillName(skill);
  const testid = `home-ring-${skill.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`;
  return (
    <div className="flex flex-col items-center gap-2 min-w-0" data-testid={testid}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`${short}: ${count} of ${total} activities`}
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#e5eaef"
          strokeWidth={stroke}
        />
        {/* Fill — mint, rounded end, starts at 12 o'clock */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#96ded1"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeDashoffset={circumference / 4}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 400ms ease" }}
        />
        {/* Centre count */}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="15"
          fontWeight="800"
          fill="#1c2d3d"
        >
          {`${count}/${Number(total) || 0}`}
        </text>
      </svg>
      <p className="text-[10.5px] font-bold text-center text-[#4a6984] leading-tight px-0.5 max-w-[92px]">
        {short}
      </p>
    </div>
  );
};

export default function SkillRingsCard({ coverage, total }) {
  const byName = coverage?.by_transferable_skill || {};
  const denom = Number(total) || 0;
  return (
    <section data-testid="home-skill-rings-card" className="ae-card p-6 md:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
            Coverage snapshot
          </span>
        </div>
        <Link
          to="/?view=tracker"
          data-testid="home-skill-rings-tracker-link"
          className="text-xs font-bold uppercase tracking-[0.12em] text-[#4a6984]/70 hover:text-[#1c2d3d] transition-colors"
        >
          View full coverage →
        </Link>
      </div>

      {/* Centered flex-wrap ring gallery — decision (a) in the
          founder ask_human: 3-col mobile / 4-col ≥640px, seventh ring
          centres in its own row via `justify-center` on the wrap.
          Fixed-width items (rather than percentage bases) so packing
          is deterministic at 390px and does not fight sub-pixel
          rounding — three 88px rings + two 12px gaps = 288px fit
          inside the card's 294px inner width. */}
      <div
        data-testid="home-skill-rings-grid"
        className="flex flex-wrap justify-center gap-x-3 gap-y-5 sm:gap-x-6 sm:gap-y-6"
      >
        {CANONICAL_SKILLS.map((skill) => (
          <div key={skill} className="w-[88px] flex justify-center">
            <Ring skill={skill} count={_resolveCount(byName, skill)} total={denom} />
          </div>
        ))}
      </div>
    </section>
  );
}
